import { useContext, useEffect, useMemo, useState } from 'react'

import {
  Box,
  Button,
  Icon,
  Image,
  Input,
  Modal,
  Select,
  Text,
  TextArea,
} from '@fairfashion/design-system'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { IProductDetails } from '../../../../sdk/products/products.types'
import { ProductsContext } from '../../../Products/products.context'

import {
  CreateProductForm,
  CreateProductModalProps,
  CreateProductStep,
} from './create-product-modal.types'

export const CreateProductModal = ({
  open,
  onClose,
}: CreateProductModalProps) => {
  const [code, setCode] = useState('')
  const [step, setStep] = useState<CreateProductStep>('initial')

  const [loading, setLoading] = useState(false)

  const [foundProduct, setFoundProduct] = useState<IProductDetails>()

  const { finishedItemCategories, brands, actions } =
    useContext(ProductsContext)

  const {
    register,
    control,
    reset,
    setValue,
    handleSubmit,
    setError,
    clearErrors,
    formState,
  } = useForm<CreateProductForm>()

  const { errors } = formState

  const navigate = useNavigate()

  useEffect(() => {
    if (open) {
      setStep('initial')
      setCode('')
      setFoundProduct(undefined)
      setLoading(false)
      reset({
        code: '',
        name: '',
        description: '',
        finishedItemCategoryId: '',
      })
    }
  }, [open])

  const handleFetchProduct = async () => {
    if (!code) {
      setError('code', { message: 'Campo obrigatório' })
      return
    }
    try {
      setLoading(true)
      const product = await actions.getProductByCode(code)
      setFoundProduct(product)
      setStep('found')
    } catch (err) {
      setValue('code', code)
      setStep('new')
    } finally {
      setLoading(false)
    }
  }
  const handleGoToProductPage = (code: string) => {
    onClose()
    navigate(`/products/${code}`)
  }

  const categoriesOptions = useMemo(
    () =>
      finishedItemCategories.map((category) => ({
        label: category.name,
        value: category.id,
      })),
    [finishedItemCategories],
  )

  const brandsOptions = useMemo(
    () =>
      brands.map((brand) => ({
        label: brand.name,
        value: brand.id,
      })),
    [brands],
  )

  const saveNewProduct = async (data: CreateProductForm) => {
    try {
      setLoading(true)
      await actions.createProduct(data)
      navigate(`/products/${data.code}`)
      onClose()
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  const renderStep = () => {
    if (step === 'initial') {
      return (
        <Box width="100%" direction="column" gap={3} paddingX={1}>
          <Box width="100%" direction="column">
            <Input
              label="Código SKU ou Identificador do Produto"
              placeholder="ABC123123123"
              onChange={(e) => {
                setCode(e.target.value.trim())
                clearErrors('code')
              }}
              value={code}
              state={errors.code ? 'error' : 'default'}
            />
            {errors.code && (
              <Text size="small" color="error">
                *{errors.code.message}
              </Text>
            )}
          </Box>
          <Box width="100%" justifyContent="flex-end">
            <Button onClick={handleFetchProduct} loading={loading}>
              Avançar
            </Button>
          </Box>
        </Box>
      )
    }

    if (step === 'found') {
      return (
        <Box width="100%" direction="column" gap={3} marginTop={3}>
          <Text>Já existe um produto com esse SKU</Text>

          <Box width="100%" gap={3}>
            <Box width="150px" height="150px">
              <Image
                alt="Product image"
                src={foundProduct?.images?.[0] || '/assets/default-image.png'}
                objectFit="cover"
              />
            </Box>
            <Box flex={1} gap={2} width="100%" direction="column">
              <Box direction="column">
                <Text size="small" fontWeight={500}>
                  SKU ou Identificador do Produto
                </Text>
                <Text fontWeight={200}>{foundProduct?.code}</Text>
              </Box>
              <Box direction="column">
                <Text size="small" fontWeight={500}>
                  Descrição
                </Text>
                <Text>{foundProduct?.description}</Text>
              </Box>
            </Box>
          </Box>
          <Box width="100%" justifyContent="flex-end" gap={1}>
            <Button onClick={onClose} variant="outlined">
              Cancelar
            </Button>
            <Button
              padding="10px 16px"
              onClick={() => handleGoToProductPage(foundProduct?.code || '')}
            >
              <Box gap={1}>
                <Text color="white" fontWeight={500}>
                  Ver Produto
                </Text>
                <Icon name="arrow_right_up" size={20} color="white" />
              </Box>
            </Button>
          </Box>
        </Box>
      )
    }

    if (step === 'new') {
      return (
        <form onSubmit={handleSubmit(saveNewProduct)}>
          <Box width="100%" direction="column" gap={2} padding={1}>
            <Input
              label="Código SKU ou Identificador do Produto"
              {...register('code')}
              disabled
            />
            <Box width="100%" gap={2}>
              <Box flex={1}>
                <Input label="Nome do Produto" {...register('name')} required />
              </Box>
              <Box flex={1}>
                <Controller
                  name="finishedItemCategoryId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label="Categoria"
                      options={categoriesOptions}
                      placeholder="Selecione uma categoria"
                      required
                      {...field}
                    />
                  )}
                />
              </Box>
            </Box>
            <Box width="100%">
              <Controller
                name="brandId"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Marca"
                    options={brandsOptions}
                    placeholder="Selecione uma marca"
                    state={errors.brandId ? 'error' : 'default'}
                    required
                    {...field}
                  />
                )}
              />
            </Box>
            <TextArea required label="Descrição" {...register('description')} />

            <Box width="100%" justifyContent="flex-end" gap={1}>
              <Button onClick={onClose} variant="outlined">
                Cancelar
              </Button>
              <Button type="submit" loading={loading}>
                Salvar
              </Button>
            </Box>
          </Box>
        </form>
      )
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Criar novo produto"
      width="600px"
      maxWidth="90%"
    >
      {renderStep()}
    </Modal>
  )
}
